import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Pencil, Hash, Hexagon, Plus, Filter as FilterIcon } from "lucide-react";
import Icon from "shared/components/Icon";
import EventPropertyFilterModule from "features/activity/components/EventPropertyFilterModule";
import { EventPropertyFilterChip } from "features/activity/components/EventsFilterModule";
import EventsSelectorMenu from "./EventsSelectorMenu";
import { getFixedPanelPositionFromRect } from "./eventSelectorGeometry";
import axiosInstance from "shared/services/api/axiosInstance";

/**
 * MetricBlock
 * Represents a single metric (A, B, C...) that can contain multiple events.
 * All events are aggregated together.
 */
export default function MetricBlock({
  metric,
  metricIndex,
  availableEvents = [],
  teamId,
  onUpdate,
  onRemove,
  onAddEvent,
  onRemoveEvent,
  onUpdateEventFilters,
  /** When true once (e.g. after “Add metric”), open the event picker without an extra click. */
  openEventPickerRequest = false,
  onOpenEventPickerRequestConsumed = null,
}) {
  const [eventMenuOpen, setEventMenuOpen] = useState(false);
  const [eventMenuAnchorRect, setEventMenuAnchorRect] = useState(null);
  const [isPropertyFilterOpen, setIsPropertyFilterOpen] = useState(false);
  const [selectedEventForFilter, setSelectedEventForFilter] = useState(null);
  const [selectedEventIndex, setSelectedEventIndex] = useState(null);
  const [editingPropertyFilter, setEditingPropertyFilter] = useState(null);
  const [isEditingMetricName, setIsEditingMetricName] = useState(false);
  const [editingMetricNameValue, setEditingMetricNameValue] = useState("");
  const metricNameInputRef = useRef(null);

  const addEventButtonRef = useRef(null);
  const eventMenuRef = useRef(null);
  const subjectTriggerRef = useRef(null);
  const subjectMenuRef = useRef(null);
  const aggTriggerRef = useRef(null);
  const aggMenuRef = useRef(null);
  const filterButtonRefs = useRef({});
  const propertyFilterButtonRefs = useRef({});

  /** null | 'subject' | 'aggregation' */
  const [metricPickerOpen, setMetricPickerOpen] = useState(null);

  // When the metric has any agent / span entity, only count + uniques are
  // supported (sessions / property aggregations don't fit the entity model).
  const hasEntityEvent = useMemo(
    () => Array.isArray(metric?.events) && metric.events.some((e) => e && (e.entityKind === 'agent' || e.entityKind === 'span')),
    [metric?.events],
  );
  const aggregations = useMemo(() => {
    const all = [
      { id: "count", label: hasEntityEvent ? "Total" : "Total Events" },
      { id: "uniques", label: "Unique Users" },
      { id: "sessions", label: "Total Sessions" },
    ];
    return hasEntityEvent ? all.filter((a) => a.id !== 'sessions') : all;
  }, [hasEntityEvent]);

  // Auto-correct an invalid aggregation if the user adds an entity event
  // while sessions was selected.
  useEffect(() => {
    if (hasEntityEvent && metric?.aggregation === 'sessions') {
      onUpdate(metricIndex, { ...metric, aggregation: 'count' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEntityEvent, metric?.aggregation]);

  const [groupKeyOptions, setGroupKeyOptions] = useState([]);

  useEffect(() => {
    const raw =
      teamId != null && teamId !== ""
        ? typeof teamId === "object" && teamId !== null
          ? teamId._id ?? teamId.id
          : teamId
        : null;
    if (raw == null || raw === "") {
      setGroupKeyOptions([]);
      return undefined;
    }
    let cancelled = false;
    axiosInstance
      .get("/group-identities/keys", { params: { team_id: raw } })
      .then((res) => {
        if (cancelled || !res.data?.success) return;
        setGroupKeyOptions(Array.isArray(res.data.keys) ? res.data.keys : []);
      })
      .catch(() => {
        if (!cancelled) setGroupKeyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const countSubjects = useMemo(() => {
    const base = [{ id: "user", label: "User", groupKey: null }];
    const fromApi = (groupKeyOptions || []).map((k) => ({
      id: `group:${k.group_key}`,
      label: k.group_key,
      groupKey: k.group_key,
    }));
    return [...base, ...fromApi];
  }, [groupKeyOptions]);

  // Parent asks to open the event menu (portal + anchor run in effects below)
  useEffect(() => {
    if (!openEventPickerRequest) return;
    setEventMenuOpen(true);
    onOpenEventPickerRequestConsumed?.();
  }, [openEventPickerRequest, onOpenEventPickerRequestConsumed]);

  // Portal position + keep aligned on scroll/resize (sidebar is overflow-auto; absolute menus were clipped)
  useLayoutEffect(() => {
    if (!eventMenuOpen) {
      setEventMenuAnchorRect(null);
      return;
    }
    const update = () => {
      const el = addEventButtonRef.current;
      if (!el) return;
      setEventMenuAnchorRect(getFixedPanelPositionFromRect(el.getBoundingClientRect()));
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [eventMenuOpen]);

  // Close popovers on outside click / Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (eventMenuOpen) {
        const inMenu = eventMenuRef.current?.contains(e.target);
        const inTrigger = addEventButtonRef.current?.contains(e.target);
        if (!inMenu && !inTrigger) setEventMenuOpen(false);
      }
      if (metricPickerOpen) {
        const inSubject =
          subjectTriggerRef.current?.contains(e.target) || subjectMenuRef.current?.contains(e.target);
        const inAgg =
          aggTriggerRef.current?.contains(e.target) || aggMenuRef.current?.contains(e.target);
        if (!inSubject && !inAgg) setMetricPickerOpen(null);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (eventMenuOpen) setEventMenuOpen(false);
        if (metricPickerOpen) setMetricPickerOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [eventMenuOpen, metricPickerOpen]);

  const metricId = String.fromCharCode(65 + metricIndex); // A, B, C...

  const handleAddEventClick = () => setEventMenuOpen((o) => !o);

  const handleSelectEvent = (eventName, kindOrSource = 'event', opts = {}) => {
    onAddEvent(metricIndex, eventName, kindOrSource, opts);
    setEventMenuOpen(false);
  };

  const handleAggregationChange = (aggId) => {
    const next = { ...metric, aggregation: aggId };
    if (aggId !== "uniques" && metric.countSubject === "group") {
      next.countSubject = "user";
      next.accumulationGroupKey = undefined;
    }
    onUpdate(metricIndex, next);
    setMetricPickerOpen(null);
  };

  const handleCountSubjectChange = (subjectRow) => {
    const next = {
      ...metric,
      countSubject: subjectRow.groupKey ? "group" : "user",
      accumulationGroupKey: subjectRow.groupKey || undefined,
    };
    if (subjectRow.groupKey && (metric.aggregation || "count") !== "uniques") {
      next.aggregation = "uniques";
    }
    onUpdate(metricIndex, next);
    setMetricPickerOpen(null);
  };

  const handleFilterClick = (eventDef, eventIndex) => {
    const source = eventDef.source || 'event';
    setSelectedEventForFilter({
      id: `${eventDef.name}\0${source}`,
      eventName: eventDef.name,
      eventType: 'custom',
      source,
    });
    setSelectedEventIndex(eventIndex);
    setIsPropertyFilterOpen(true);
  };

  const handleFiltersChange = (filter) => {
    if (selectedEventIndex !== null && selectedEventForFilter) {
      // Get current filters for this event
      const currentEvent = metric.events[selectedEventIndex];
      const currentFilters = currentEvent.filters || [];
      
      // Convert filter from EventPropertyFilterModule format to insights format
      const insightsFilter = {
        field: filter.property || filter.field,
        property: filter.property || filter.field, // queryCompiler accepts both
        operator: filter.operator,
        value: filter.value,
        value2: filter.value2,
        propertyType: filter.propertyType,
        propertySource: filter.propertySource,
        propertyLabel: filter.propertyLabel || filter.property || filter.field,
        category: 'event_properties',
        eventName: selectedEventForFilter.eventName,
        listMode: filter.listMode,
        listItemType: filter.listItemType,
        sampleValues: filter.sampleValues || [],
        id: filter.id || `filter-${Date.now()}-${Math.random()}`,
        connector: currentFilters.length === 0 ? 'WHERE' : 'AND' // First filter is WHERE, others are AND
      };
      
      // If editing existing filter
      if (editingPropertyFilter) {
        const updatedFilters = currentFilters.map(f => 
          f.id === editingPropertyFilter.id ? insightsFilter : f
        );
        onUpdateEventFilters(metricIndex, selectedEventIndex, updatedFilters);
        setEditingPropertyFilter(null);
      } else {
        // Add new filter
        onUpdateEventFilters(metricIndex, selectedEventIndex, [...currentFilters, insightsFilter]);
      }
      
      setIsPropertyFilterOpen(false);
      setSelectedEventForFilter(null);
      setSelectedEventIndex(null);
    }
  };

  const handleFirstTimeChange = (enabled, eventName, eventId) => {
    if (selectedEventIndex !== null && selectedEventForFilter) {
      const currentEvent = metric.events[selectedEventIndex];
      const currentFilters = currentEvent.filters || [];
      
      if (enabled) {
        // Remove any existing first_time filter first
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        // Add the first time filter
        const firstTimeFilter = {
          id: 'first-time-filter',
          field: 'firstTime',
          property: 'firstTime',
          operator: 'equals', // Required for query compiler, but firstTime is handled specially
          value: true,
          category: 'first_time',
          eventName: selectedEventForFilter.eventName,
          connector: withoutFirstTime.length === 0 ? 'WHERE' : 'AND'
        };
        onUpdateEventFilters(metricIndex, selectedEventIndex, [...withoutFirstTime, firstTimeFilter]);
      } else {
        // Remove the first time filter
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        onUpdateEventFilters(metricIndex, selectedEventIndex, withoutFirstTime);
      }
      
      setIsPropertyFilterOpen(false);
      setSelectedEventForFilter(null);
      setSelectedEventIndex(null);
    }
  };

  const metricLabel = (() => {
    const aggLabel =
      aggregations.find((a) => a.id === metric.aggregation)?.label || "Total Events";
    const events = metric.events || [];
    if (events.length === 0) return `${aggLabel} of ...`;
    if (events.length === 1) return `${aggLabel} of ${events[0].name}`;
    return `${aggLabel} of ${events[0].name} or ${events.length - 1} more`;
  })();

  const displayLabel = (metric.metricName && String(metric.metricName).trim()) ? String(metric.metricName).trim() : metricLabel;

  const startEditingName = () => {
    setEditingMetricNameValue(displayLabel);
    setIsEditingMetricName(true);
    setTimeout(() => metricNameInputRef.current?.focus(), 0);
  };

  const commitMetricName = () => {
    const value = (editingMetricNameValue || "").trim();
    onUpdate(metricIndex, { ...metric, metricName: value || undefined });
    setIsEditingMetricName(false);
    setEditingMetricNameValue("");
  };

  useEffect(() => {
    if (!isEditingMetricName) return;
    const ref = metricNameInputRef.current;
    if (!ref) return;
    const onBlur = () => {
      const raw = ref.value ?? "";
      const value = (typeof raw === "string" ? raw : "").trim();
      onUpdate(metricIndex, { ...metric, metricName: value || undefined });
      setIsEditingMetricName(false);
      setEditingMetricNameValue("");
    };
    ref.addEventListener("blur", onBlur);
    return () => ref.removeEventListener("blur", onBlur);
  }, [isEditingMetricName, metricIndex, metric, onUpdate]);

  const pickerItemClass =
    "block w-full truncate px-2.5 py-1.5 text-left text-[11px] transition-colors rounded-sm";
  const pickerMenuClass =
    "absolute left-0 top-full z-50 mt-0.5 inline-block max-w-[min(100vw-2rem,16rem)] overflow-hidden rounded-lg border py-1 shadow-lg";
  const subjectLabel =
    metric.countSubject === "group" && metric.accumulationGroupKey
      ? `Group · ${metric.accumulationGroupKey}`
      : "User";

  const isCountSubjectRowActive = (s) => {
    if (s.groupKey) {
      return metric.countSubject === "group" && metric.accumulationGroupKey === s.groupKey;
    }
    return (metric.countSubject || "user") === "user";
  };
  const aggLabel =
    aggregations.find((a) => a.id === (metric.aggregation || "count"))?.label || "Total Events";

  return (
    <div
      className="mb-3 rounded-md border border-dotted p-1.5"
      style={{ borderColor: "var(--border-default)", backgroundColor: "transparent" }}
    >
      <div className="group/mh mb-1.5 flex min-h-[28px] items-center gap-1.5">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-dotted text-[11px] font-semibold"
          style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
        >
          {metricId}
        </span>
        {isEditingMetricName ? (
          <input
            ref={metricNameInputRef}
            type="text"
            value={editingMetricNameValue}
            onChange={(e) => setEditingMetricNameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitMetricName();
              if (e.key === "Escape") {
                setEditingMetricNameValue(displayLabel);
                setIsEditingMetricName(false);
                metricNameInputRef.current?.blur();
              }
            }}
            className="min-w-0 flex-1 rounded border px-2 py-0.5 text-[12px] leading-snug"
            style={{ color: "var(--text-primary)", borderColor: "var(--border-default)", backgroundColor: "transparent" }}
            placeholder={metricLabel}
          />
        ) : (
          <>
            <span className="min-w-0 flex-1 truncate text-[12px] font-medium leading-snug" style={{ color: "var(--text-secondary)" }}>
              {displayLabel}
            </span>
            <div className="flex shrink-0 items-center gap-0 opacity-0 transition-opacity group-hover/mh:opacity-100">
              <button
                type="button"
                onClick={startEditingName}
                className="rounded p-0.5 hover:opacity-70"
                title="Rename metric"
              >
                <Pencil className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
              </button>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(metricIndex)}
                  className="rounded p-0.5 hover:opacity-70"
                  title="Remove metric"
                >
                  <Icon name="delete" size={14} color="var(--text-muted)" />
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Indent below header so events / add / selectors line up past the A badge */}
      <div className="mt-0.5 ml-[30px] pl-0.5">
      {(metric.events || []).map((eventDef, eventIndex) => {
        const hasFilters = eventDef.filters && eventDef.filters.length > 0;
        const eventFilters = eventDef.filters || [];

        return (
          <div
            key={`${eventDef.name}\0${eventDef.source || "event"}-${eventIndex}`}
            className="border-b border-dotted last:border-b-0"
            style={{ borderColor: "var(--border-default)" }}
          >
            <div className="group/erow flex items-center gap-1.5 px-0.5 py-1">
              <Hexagon className="h-3.5 w-3.5 shrink-0 text-[color:var(--trodo-primary)]" strokeWidth={2} />
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-snug text-ink">
                {eventDef.name}
              </span>
              {hasFilters && (
                <span className="shrink-0 rounded px-1 py-px text-[10px] font-semibold leading-none text-ink-onBrand" style={{ backgroundColor: "var(--brand-primary)" }}>
                  {eventFilters.length}
                </span>
              )}
              <div className="flex shrink-0 items-center gap-0 opacity-0 transition-opacity group-hover/erow:opacity-100">
                <button
                  ref={(el) => {
                    if (el) filterButtonRefs.current[`${metricIndex}-${eventIndex}`] = el;
                  }}
                  type="button"
                  onClick={() => handleFilterClick(eventDef, eventIndex)}
                  className="rounded p-0.5 hover:opacity-70"
                  title="Filter event"
                  style={{ color: hasFilters ? "var(--brand-primary)" : "var(--text-muted)" }}
                >
                  <FilterIcon size={14} color={hasFilters ? "var(--brand-primary)" : "var(--text-muted)"} />
                </button>
                <button
                  type="button"
                  onClick={() => onRemoveEvent(metricIndex, eventIndex)}
                  className="rounded p-0.5 hover:opacity-70"
                  title="Remove event"
                >
                  <Icon name="delete" size={14} color="var(--text-muted)" />
                </button>
              </div>
            </div>

            {eventFilters.length > 0 && (
              <div className="space-y-0.5 border-t border-dotted px-0.5 pb-0.5 pl-4 pt-0.5" style={{ borderColor: "var(--border-default)" }}>
                {eventFilters.map((filter, filterIndex) => {
                  const connector = filter.connector || (filterIndex === 0 ? "WHERE" : "AND");

                  if (filter.category === "first_time") {
                    return (
                      <div key={filter.id} className="flex flex-wrap items-center gap-1">
                        <span className="px-0.5 text-[10px] font-medium uppercase leading-none" style={{ color: "var(--text-muted)" }}>
                          {connector}
                        </span>
                        <div
                          className="inline-flex items-center gap-1 rounded border border-dotted px-1.5 py-0.5"
                          style={{ borderColor: "var(--border-default)" }}
                        >
                          <span className="text-[11px] font-medium leading-snug" style={{ color: "var(--text-primary)" }}>
                            First time only
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedFilters = eventFilters.filter((f) => f.id !== filter.id);
                              onUpdateEventFilters(metricIndex, eventIndex, updatedFilters);
                            }}
                            className="rounded p-0.5 hover:opacity-70"
                            title="Remove First time only"
                          >
                            <Icon name="delete" size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  }

                    if (!propertyFilterButtonRefs.current[filter.id]) {
                      propertyFilterButtonRefs.current[filter.id] = React.createRef();
                    }
                    const onRemoveFilter = () => {
                      const updatedFilters = eventFilters.filter((f) => f.id !== filter.id);
                      onUpdateEventFilters(metricIndex, eventIndex, updatedFilters);
                    };
                    return (
                      <EventPropertyFilterChip
                        key={filter.id}
                        filter={filter}
                        compact
                        isFirst={filterIndex === 0}
                        filterRef={propertyFilterButtonRefs.current[filter.id]}
                        onEdit={() => {
                          setEditingPropertyFilter(filter);
                          setSelectedEventForFilter({
                            id: eventDef.name,
                            eventName: eventDef.name,
                            eventType: "custom",
                          });
                          setSelectedEventIndex(eventIndex);
                          setIsPropertyFilterOpen(true);
                        }}
                        onUpdate={(updates) => {
                          const updatedFilters = eventFilters.map((f) =>
                            f.id === filter.id ? { ...f, ...updates } : f
                          );
                          onUpdateEventFilters(metricIndex, eventIndex, updatedFilters);
                        }}
                        onRemove={onRemoveFilter}
                        teamId={teamId}
                        eventName={eventDef.name}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

      <div className="relative border-t border-dotted pt-1" style={{ borderColor: "var(--border-default)" }}>
        <button
          ref={addEventButtonRef}
          type="button"
          onClick={handleAddEventClick}
          className="flex w-full items-center gap-1 px-0.5 py-1 text-left transition-opacity hover:opacity-80"
          style={{ color: "var(--text-muted)" }}
        >
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dotted"
            style={{ borderColor: "var(--border-default)" }}
            aria-hidden
          >
            <Plus className="h-3 w-3" strokeWidth={2.5} />
          </span>
          <span className="text-[12px] font-medium leading-snug" style={{ color: "var(--text-secondary)" }}>
            Add Event
          </span>
        </button>
        {eventMenuOpen &&
          eventMenuAnchorRect &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              ref={eventMenuRef}
              style={{
                position: "fixed",
                top: eventMenuAnchorRect.top,
                left: eventMenuAnchorRect.left,
                width: eventMenuAnchorRect.width,
                maxHeight: eventMenuAnchorRect.maxHeightPx ?? 480,
                zIndex: 10000,
              }}
              className="flex max-h-[min(65vh,calc(100vh-24px))] flex-col overflow-hidden"
            >
              <EventsSelectorMenu
                availableEvents={
                  Array.isArray(availableEvents)
                    ? availableEvents.filter(
                        (evt) =>
                          !(metric.events || []).some(
                            (e) =>
                              e.name === evt.name && (e.source || "event") === (evt.source || "event")
                          )
                      )
                    : []
                }
                onSelect={(name, kind, opts) => handleSelectEvent(name, kind, opts)}
                onClose={() => setEventMenuOpen(false)}
                maxHeight={eventMenuAnchorRect.maxHeightPx ?? 480}
              />
            </div>,
            document.body
          )}
      </div>

      <div
        className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-dotted pt-1.5"
        style={{ borderColor: "var(--border-default)" }}
      >
        <div className="inline-flex items-center gap-1.5">
          <Hash
            className="h-3 w-3 shrink-0 text-ink"
            strokeWidth={2.25}
            aria-hidden
          />
          <div className="relative">
            <button
              ref={subjectTriggerRef}
              type="button"
              aria-expanded={metricPickerOpen === "subject"}
              aria-haspopup="listbox"
              aria-label="Count by"
              onClick={() => setMetricPickerOpen((p) => (p === "subject" ? null : "subject"))}
              className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
              style={{ fontSize: "11px" }}
            >
              {subjectLabel}
              <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
            </button>
            {metricPickerOpen === "subject" && (
              <div
                ref={subjectMenuRef}
                role="listbox"
                className={`${pickerMenuClass} shadow-token-lg`}
                style={{
                  backgroundColor: "var(--surface-base)",
                  borderColor: "var(--border-default)",
                }}
              >
                {countSubjects.map((s) => {
                  const active = isCountSubjectRowActive(s);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      title={s.label}
                      onClick={() => handleCountSubjectChange(s)}
                      className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                      style={{
                        color: active ? "var(--text-primary)" : "var(--text-secondary)",
                        backgroundColor: "transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="relative">
          <button
            ref={aggTriggerRef}
            type="button"
            aria-expanded={metricPickerOpen === "aggregation"}
            aria-haspopup="listbox"
            aria-label="Accumulate metric"
            onClick={() => setMetricPickerOpen((p) => (p === "aggregation" ? null : "aggregation"))}
            className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
            style={{ fontSize: "11px" }}
          >
            {aggLabel}
            <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
          </button>
          {metricPickerOpen === "aggregation" && (
            <div
              ref={aggMenuRef}
              role="listbox"
              className={`${pickerMenuClass} shadow-token-lg`}
              style={{
                backgroundColor: "var(--surface-base)",
                borderColor: "var(--border-default)",
              }}
            >
              {aggregations.map((a) => {
                const active = a.id === (metric.aggregation || "count");
                return (
                  <button
                    key={a.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    title={a.label}
                    onClick={() => handleAggregationChange(a.id)}
                    className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                    style={{
                      color: active ? "var(--text-primary)" : "var(--text-secondary)",
                      backgroundColor: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Event Property Filter Module */}
      {isPropertyFilterOpen && selectedEventForFilter && (
        <EventPropertyFilterModule
          isOpen={isPropertyFilterOpen}
          onClose={() => {
            setIsPropertyFilterOpen(false);
            setSelectedEventForFilter(null);
            setSelectedEventIndex(null);
            setEditingPropertyFilter(null);
          }}
          triggerRef={
            editingPropertyFilter && propertyFilterButtonRefs.current[editingPropertyFilter.id]
              ? propertyFilterButtonRefs.current[editingPropertyFilter.id]
              : filterButtonRefs.current[`${metricIndex}-${selectedEventIndex}`]
          }
          eventName={selectedEventForFilter.eventName}
          eventId={selectedEventForFilter.id}
          teamId={teamId}
          filters={selectedEventIndex !== null ? (metric.events[selectedEventIndex]?.filters || []) : []}
          onFiltersChange={handleFiltersChange}
          existingFilter={editingPropertyFilter}
          firstTimeOnly={selectedEventIndex !== null ? (metric.events[selectedEventIndex]?.filters || []).some(f => f.category === 'first_time') : false}
          onFirstTimeChange={handleFirstTimeChange}
        />
      )}
    </div>
  );
}

